import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

const COLORS = {
  burgundy: "#5B0E14",
  gold: "#F1E194",
  cream: "#FDF6E3",
  goldLight: "#F1E19430",
  burgundyLight: "#5B0E1410",
};

const SOURCE_LABELS = {
  claude: { label: "Claude Code", icon: "🟣", color: "#7C3AED" },
  claude_code: { label: "Claude Code", icon: "🟣", color: "#7C3AED" },
  cursor: { label: "Cursor", icon: "🟢", color: "#10B981" },
  vscode: { label: "VS Code", icon: "🔵", color: "#3B82F6" },
  codex: { label: "Codex", icon: "⚫", color: "#374151" },
  windsurf: { label: "Windsurf", icon: "🟠", color: "#F97316" },
  mcp: { label: "MCP", icon: "🔗", color: "#6366F1" },
  api: { label: "API", icon: "⚙️", color: "#6B7280" },
  dashboard: { label: "Dashboard", icon: "🖥️", color: "#0EA5E9" },
};

function SourceBadge({ source }) {
  const info = SOURCE_LABELS[source] || { label: source || "—", icon: "❓", color: "#9CA3AF" };
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full"
      style={{ background: info.color + "20", color: info.color }}>
      {info.icon} {info.label}
    </span>
  );
}

function timeAgo(isoStr) {
  if (!isoStr) return null;
  try {
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return "الآن";
    if (diff < 3600) return `منذ ${Math.floor(diff / 60)} دقيقة`;
    if (diff < 86400) return `منذ ${Math.floor(diff / 3600)} ساعة`;
    return `منذ ${Math.floor(diff / 86400)} يوم`;
  } catch {
    return null;
  }
}

export default function AIClientsPanel({ token }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);
  const [filterOnline, setFilterOnline] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/ai-clients`, { headers });
      setClients(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "خطأ في تحميل بيانات العملاء");
    }
    setLoading(false);
  };

  const revokeKey = async (staff) => {
    if (!window.confirm(`هل تريد إلغاء مفتاح API لـ ${staff.display_name}؟`)) return;
    setRevokingId(staff.id);
    try {
      await axios.delete(`${API}/admin/staff/${staff.id}/api-key`, { headers });
      toast.success(`تم إلغاء مفتاح ${staff.display_name}`);
      loadClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل إلغاء المفتاح");
    }
    setRevokingId(null);
  };

  const filtered = filterOnline ? clients.filter((c) => c.is_online) : clients;

  const stats = {
    total: clients.length,
    withKey: clients.filter((c) => c.api_key_active).length,
    online: clients.filter((c) => c.is_online).length,
    totalRequests: clients.reduce((s, c) => s + (c.api_key_requests || 0), 0),
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black" style={{ color: COLORS.burgundy }}>🌐 AI Clients</h2>
          <p className="text-sm mt-1" style={{ color: COLORS.burgundy + "80" }}>
            عملاء AI المتصلون — عرض الـ super admin
          </p>
        </div>
        <button
          onClick={loadClients}
          className="px-4 py-2 rounded-full text-sm font-bold transition-all"
          style={{ background: COLORS.burgundyLight, color: COLORS.burgundy }}>
          ↻ تحديث
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "إجمالي الموظفين", value: stats.total, color: COLORS.burgundy },
          { label: "مفاتيح نشطة", value: stats.withKey, color: "#16a34a" },
          { label: "متصل الآن", value: stats.online, color: "#2563eb" },
          { label: "إجمالي الطلبات", value: stats.totalRequests, color: "#d97706" },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl p-4 text-center bg-white" style={{ border: `1px solid ${COLORS.gold}50` }}>
            <div className="text-3xl font-black" style={{ color: s.color }}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilterOnline(false)}
          className="px-4 py-2 rounded-full text-sm font-bold transition-all"
          style={{
            background: !filterOnline ? COLORS.burgundy : COLORS.burgundyLight,
            color: !filterOnline ? "#fff" : COLORS.burgundy,
          }}>
          الكل ({clients.length})
        </button>
        <button
          onClick={() => setFilterOnline(true)}
          className="px-4 py-2 rounded-full text-sm font-bold transition-all"
          style={{
            background: filterOnline ? "#16a34a" : "#16a34a10",
            color: filterOnline ? "#fff" : "#16a34a",
          }}>
          متصل الآن ({stats.online})
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">⏳ جارٍ التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">لا توجد بيانات</div>
      ) : (
        <div className="bg-white rounded-2xl overflow-hidden" style={{ border: `1px solid ${COLORS.gold}50` }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: COLORS.cream }}>
                  {["الموظف", "المفتاح", "العميل", "آخر اتصال", "الطلبات", "الحالة", ""].map((h) => (
                    <th key={h} className="text-right px-4 py-3 font-black text-xs" style={{ color: COLORS.burgundy + "80" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((staff) => (
                  <tr key={staff.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-sm"
                          style={{ background: COLORS.burgundyLight, color: COLORS.burgundy }}>
                          {(staff.display_name || "?")[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div className="font-bold" style={{ color: COLORS.burgundy }}>
                            {staff.display_name}
                          </div>
                          <div className="text-xs text-gray-400">@{staff.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {staff.api_key_prefix ? (
                        <code className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                          {staff.api_key_prefix}
                        </code>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {staff.last_source ? (
                        <SourceBadge source={staff.last_source} />
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {timeAgo(staff.api_key_last_used) || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-black text-sm" style={{ color: COLORS.burgundy }}>
                        {staff.api_key_requests || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {!staff.api_key_active ? (
                        <span className="text-xs font-bold text-gray-400">— بدون مفتاح</span>
                      ) : staff.is_online ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                          متصل
                        </span>
                      ) : (
                        <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          Offline
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {staff.api_key_active && (
                        <button
                          onClick={() => revokeKey(staff)}
                          disabled={revokingId === staff.id}
                          className="text-xs font-bold px-2 py-1 rounded-lg transition-all disabled:opacity-40"
                          style={{ background: "#fee2e2", color: "#dc2626" }}>
                          {revokingId === staff.id ? "⏳" : "🗑 إلغاء"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
